/**
 * `Home` — pagina di ingresso (hub) su `/`, mostrata dopo l'autenticazione.
 *
 * Presenta le macro-sezioni dell'applicazione (Assessment, Review, Insights,
 * Candidati, Explore) come card su un'unica riga con una breve descrizione.
 * Ogni card porta alla home della relativa sezione. Le sezioni per cui l'utente non ha i permessi
 * sono mostrate DISABILITATE (non cliccabili), non nascoste: così l'utente
 * vede comunque l'intera offerta del prodotto.
 *
 * La visibilità/accessibilità replica (in modo difensivo) le guardie di rotta:
 * l'autorizzazione autorevole resta l'API. Su questa pagina il selettore di
 * sezione in navbar è nascosto (vedi `Navbar`), perché la home È già il punto
 * di scelta della sezione.
 */
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { SECTIONS, type SectionAccess } from '../components/sections';
import { Icon } from '../components/ui';

export default function Home() {
  const { role, me } = useAuth();

  // Contesto di accesso: identico a quello usato dal selettore di sezione.
  const access: SectionAccess = {
    role,
    levelsEnabled: role === 'admin' || me?.levelsEnabled === true,
    cvEnabled: role === 'admin' || me?.cvEnabled === true,
  };

  return (
    <section className="home" aria-labelledby="home-title">
      <div className="home__intro">
        <h1 id="home-title">Da dove vuoi iniziare?</h1>
        <p className="home__lead">
          Il portale aziendale per mappare e valutare le competenze digitali:
          registra la tua autovalutazione, gestisci le revisioni del tuo team e
          analizza la copertura delle competenze a livello di organizzazione.
        </p>
      </div>

      <div className="home__grid">
        {SECTIONS.map((section) => {
          const enabled = section.canAccess(access);
          const body = (
            <>
              <span className="home-card__icon" aria-hidden="true">
                <Icon name={section.icon} size={28} />
              </span>
              <span className="home-card__title">
                {section.label}
                {section.beta === true && (
                  <span className="home-card__beta">Beta</span>
                )}
              </span>
              <span className="home-card__description">{section.description}</span>
            </>
          );

          if (!enabled) {
            return (
              <div
                key={section.id}
                className="home-card home-card--disabled"
                aria-disabled="true"
                // Descrive lo stato per gli screen reader senza renderlo un link.
                role="group"
                aria-label={`${section.label} (accesso non disponibile)`}
              >
                {body}
              </div>
            );
          }

          return (
            <Link
              key={section.id}
              to={section.home}
              className="home-card home-card--interactive"
              aria-label={`Vai alla sezione ${section.label}`}
            >
              {body}
            </Link>
          );
        })}
      </div>

      {/* Attribuzione del modello di riferimento: le competenze, le skill e la
          scala dei livelli (e-1…e-5) seguono lo standard europeo e-CF. */}
      <footer className="home__attribution">
        <p>
          I dati di competenze, skill e livelli si basano sull'
          <strong>European e-Competence Framework (e-CF)</strong>, lo standard
          europeo <abbr title="European Committee for Standardization">CEN</abbr>{' '}
          EN 16234-1: un quadro comune di 41 competenze articolate in 5 aree e 5
          livelli di padronanza (e-1…e-5).
        </p>
      </footer>
    </section>
  );
}
